import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../styles/Categories.module.scss';
import { assetUrl } from '../../utils/imageUrl';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { getCategoriesWithDirectProducts, getHierarchicalCategories } from '../../utils/categoryTree';
import CarouselNav from '../common/CarouselNav';
import ProductImage from '../common/ProductImage';
import useSlidesToShow from '../../hooks/useSlidesToShow';
import { getCategoryUrl } from '../../utils/urls';

const Categories = ({ title, products = [], productList = null }) => {
  const sliderRef = useRef(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const visible = useMemo(() => {
    if (Array.isArray(productList)) {
      return getCategoriesWithDirectProducts(products, productList, { onlyPublished: true });
    }
    return getHierarchicalCategories(products, { onlyPublished: true });
  }, [products, productList]);
  const count = visible.length;
  const slidesToShow = useSlidesToShow(count, 'categories');
  const useCarousel = count > slidesToShow;
  const pageCount = useCarousel ? count - slidesToShow + 1 : 0;

  useEffect(() => {
    setSlideIndex(0);
    sliderRef.current?.slickGoTo(0);
  }, [slidesToShow, count]);

  const setting = useMemo(() => ({
    dots: false,
    infinite: useCarousel && count > slidesToShow,
    speed: 500,
    slidesToShow: slidesToShow || 1,
    slidesToScroll: 1,
    autoplay: useCarousel && count > 1,
    autoplaySpeed: 2000,
    arrows: false,
    pauseOnHover: true,
    beforeChange: (_current, next) => setSlideIndex(next),
  }), [count, slidesToShow, useCarousel]);

  if (count === 0) {
    return null;
  }

  const items = visible.map(({ id, name, image, depth, slug }) => (
    <div
      key={id}
      className={`${styles.item} ${depth > 0 ? styles.itemChild : ''}`}
      style={depth > 0 ? { marginLeft: `${depth * 12}px` } : undefined}
    >
      <Link to={getCategoryUrl({ id, slug })}>
        <div className={styles.image}>
          <ProductImage src={assetUrl(image)} alt={name} width={200} height={200} />
        </div>
        <h3 className={styles.title}>{depth > 0 ? `— ${name}` : name}</h3>
      </Link>
    </div>
  ));

  return (
    <section className={`${styles.section}${useCarousel ? ` ${styles.sectionCarousel}` : ''}`}>
      <h2>{title}</h2>
      {useCarousel ? (
        <>
          <div className={styles.sliderWrap}>
            <Slider key={`categories-${slidesToShow}-${count}`} ref={sliderRef} {...setting}>
              {items}
            </Slider>
          </div>
          <CarouselNav
            total={pageCount}
            current={Math.min(slideIndex, Math.max(pageCount - 1, 0))}
            onPrev={() => sliderRef.current?.slickPrev()}
            onNext={() => sliderRef.current?.slickNext()}
            onDotClick={(index) => sliderRef.current?.slickGoTo(index)}
          />
        </>
      ) : (
        <div className={styles.grid}>{items}</div>
      )}
    </section>
  );
};

export default Categories;
